/**
 * Game Mode Factory
 * 
 * Factory pattern implementation for creating game mode instances.
 * Handles dependency injection and initialization of mode-specific systems.
 */

import { BaseGameMode, ModeConfig } from './BaseGameMode'
import { Grid } from '../Grid'
import { GameBalanceManager } from '../GameBalanceManager'
import { PairGenerator } from '../FallingPair'
import { GameMode, DEFAULT_GAME_CONFIG, mergeConfig } from './GameCore'
import { EndlessMatchingEngine } from '../modes/EndlessMatchingEngine'
import { EndlessGameMode } from '../modes/EndlessGameMode'
import { TimeAttackMatchingEngine } from '../modes/TimeAttackMatchingEngine'
import { TimeAttackGameMode } from '../modes/TimeAttackGameMode'
import { PuzzleMatchingEngine } from '../modes/PuzzleMatchingEngine'
import { PuzzleGameMode, PuzzleConfig } from '../modes/PuzzleGameMode'

/**
 * Factory for creating game mode instances
 */
export class GameModeFactory {
    /**
     * Create a game mode instance
     * 
     * @param mode - The mode type to create
     * @param config - Optional configuration override
     * @returns BaseGameMode instance
     */
    public static createMode(
        mode: GameMode,
        config?: Partial<ModeConfig>
    ): BaseGameMode {
        // Merge configuration with defaults
        const fullConfig = mergeConfig(DEFAULT_GAME_CONFIG, config)

        // Initialize shared systems
        const grid = new Grid()
        const balanceManager = new GameBalanceManager()
        const pairGenerator = new PairGenerator(balanceManager)

        // Create mode-specific matching engine and mode instance
        switch (mode) {
            case GameMode.ENDLESS:
                return GameModeFactory.createEndlessMode(
                    grid,
                    pairGenerator,
                    balanceManager,
                    fullConfig
                )

            case GameMode.TIME_ATTACK:
                return GameModeFactory.createTimeAttackMode(
                    grid,
                    pairGenerator,
                    balanceManager,
                    fullConfig
                )

            case GameMode.PUZZLE:
                return GameModeFactory.createPuzzleMode(
                    grid,
                    pairGenerator,
                    balanceManager,
                    fullConfig
                )

            case GameMode.CLASSIC:
                // TODO: Implement ClassicMode (original match-3 style)
                throw new Error('Classic mode not yet implemented')

            default:
                throw new Error(`Unknown game mode: ${mode}`)
        }
    }

    /**
     * Create Endless mode instance
     * 
     * Endless mode uses Puyo-style matching with infinite progression
     */
    private static createEndlessMode(
        grid: Grid,
        pairGenerator: PairGenerator,
        balanceManager: GameBalanceManager,
        config: ModeConfig
    ): BaseGameMode {
        const matchingEngine = new EndlessMatchingEngine(grid, balanceManager)

        return new EndlessGameMode(
            grid,
            matchingEngine,
            pairGenerator,
            balanceManager,
            config
        )
    }

    /**
     * Create Time Attack mode instance
     * 
     * Time Attack mode uses Puyo-style matching with a 5-minute countdown timer
     * and score multipliers that increase with consecutive matches
     */
    private static createTimeAttackMode(
        grid: Grid,
        pairGenerator: PairGenerator,
        balanceManager: GameBalanceManager,
        config: ModeConfig
    ): BaseGameMode {
        const matchingEngine = new TimeAttackMatchingEngine(grid, balanceManager)

        return new TimeAttackGameMode(
            grid,
            matchingEngine,
            pairGenerator,
            balanceManager,
            config
        )
    }

    /**
     * Create Puzzle mode instance
     * 
     * Puzzle mode uses Dr. Mario-style line-clearing mechanics with
     * capsule dropping, virus elimination, and level progression
     */
    private static createPuzzleMode(
        grid: Grid,
        pairGenerator: PairGenerator,
        balanceManager: GameBalanceManager,
        config: ModeConfig
    ): BaseGameMode {
        const matchingEngine = new PuzzleMatchingEngine(grid, balanceManager)

        // Extract puzzle-specific config with defaults
        const puzzleSpecificConfig = config as Partial<PuzzleConfig>
        const puzzleConfig: PuzzleConfig = {
            ...config,
            maxLevel: puzzleSpecificConfig.maxLevel ?? 20,
            baseFallSpeed: puzzleSpecificConfig.baseFallSpeed ?? 90,
            speedIncreasePerLevel: puzzleSpecificConfig.speedIncreasePerLevel ?? 5
        }

        return new PuzzleGameMode(
            grid,
            matchingEngine,
            pairGenerator,
            balanceManager,
            puzzleConfig
        )
    }

    /**
     * Get available game modes
     */
    public static getAvailableModes(): GameMode[] {
        return [
            GameMode.ENDLESS,
            GameMode.TIME_ATTACK,
            GameMode.PUZZLE
            // Add more modes as they're implemented
        ]
    }

    /**
     * Check if a mode is implemented
     */
    public static isModeAvailable(mode: GameMode): boolean {
        return GameModeFactory.getAvailableModes().includes(mode)
    }

    /**
     * Get mode display name
     */
    public static getModeName(mode: GameMode): string {
        const names: Record<GameMode, string> = {
            [GameMode.ENDLESS]: 'Endless',
            [GameMode.TIME_ATTACK]: 'Time Attack',
            [GameMode.PUZZLE]: 'Puzzle',
            [GameMode.CLASSIC]: 'Classic'
        }
        return names[mode] || 'Unknown'
    }

    /**
     * Get mode description
     */
    public static getModeDescription(mode: GameMode): string {
        const descriptions: Record<GameMode, string> = {
            [GameMode.ENDLESS]: 'Puyo-style gameplay with time-based difficulty progression',
            [GameMode.TIME_ATTACK]: 'Score as many points as possible within a time limit',
            [GameMode.PUZZLE]: 'Dr. Mario-style capsule dropping with virus elimination and 20 progressive levels',
            [GameMode.CLASSIC]: 'Original match-3 style gameplay'
        }
        return descriptions[mode] || 'No description available'
    }
}
